import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import axios from "axios";
import {
  BankerAlert,
  MessagePopup,
  OverlayCrawlBanner,
  ResultsOverlay,
  WeatherFullscreen,
  FullscreenOdds,
  FullscreenBong,
  OverlayOddsBox,
  OverlayBongDisplay,
  OverlaySuperLowerThird,
  OverlayNavnsuperLowerThird,
  OpeningPoster,
  ClosingPoster,
} from "../components/overlay";
import { WebcamBackground } from "../components/overlay/WebcamBackground";
import { ClownOverlay, KingOverlay } from "../components/overlay/ClownOverlay";
import { V75Popup } from "../components/overlay/V75Popup";
import { VignettOverlay } from "../components/overlay/VignettOverlay";
import { V75Vignett } from "../components/overlay/V75Vignett";
import { StudioFrame, STUDIO_CAM_RECT } from "../components/overlay/StudioFrame";
import { LoopVideoLayer } from "../components/LoopVideoLayer";
import { WipeTransition } from "../components/WipeTransition";

const WIPE_COVER_TIME = 1.1; // seconds — moment the gold ribbons fully cover screen

// Always use relative URLs - works on both preview and deployed domains
const API = '/api';

// Preload logo to prevent layout jump in animations
const logoPreload = new Image();
logoPreload.src = '/sorlandet-logo.png';

export default function Overlay() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const isVmix = location.pathname === '/vmix';
  const [raceData, setRaceData] = useState(null);
  const [gameType, setGameType] = useState("");
  const [legNumber, setLegNumber] = useState(1);
  const [trackName, setTrackName] = useState("");
  const [raceDistance, setRaceDistance] = useState(0);
  const [startMethod, setStartMethod] = useState("Volte");
  const [startTime, setStartTime] = useState(null);
  const [flashingHorses, setFlashingHorses] = useState(new Set());
  const [currentGameId, setCurrentGameId] = useState(null);

  // Distribution change tracking (arrows)
  const prevDistRef = useRef({});
  const [distChanges, setDistChanges] = useState({});

  // Banker alert state
  const [showBanker, setShowBanker] = useState(false);
  const [bankerHorse, setBankerHorse] = useState(null);
  const [bankerVisible, setBankerVisible] = useState(false);

  // Message popup state
  const [showMessage, setShowMessage] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [messageVisible, setMessageVisible] = useState(false);
  const [messageExiting, setMessageExiting] = useState(false);

  // V75 popup state
  const [v75Text, setV75Text] = useState('');
  const [showV75, setShowV75] = useState(false);
  const [v75Visible, setV75Visible] = useState(false);
  const [v75Exiting, setV75Exiting] = useState(false);

  // Results overlay state
  const [showResults, setShowResults] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const [resultsVisible, setResultsVisible] = useState(false);

  const [currentLeg, setCurrentLeg] = useState(1);
  const [displayMode, setDisplayMode] = useState('overlay');
  const [showBong, setShowBong] = useState(false);
  const [bongData, setBongData] = useState(null);
  const [bongDataGuest, setBongDataGuest] = useState(null);
  const [bongSelectedLegs, setBongSelectedLegs] = useState([]);
  const [showOdds, setShowOdds] = useState(true);
  const [fullscreenContent, setFullscreenContent] = useState('odds');
  const [greenOddsLeaving, setGreenOddsLeaving] = useState(false);
  const [greenOddsActive, setGreenOddsActive] = useState(false);
  const [greenBongLeaving, setGreenBongLeaving] = useState(false);
  const [greenBongActive, setGreenBongActive] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Super/Lower Third state
  const [showSuper, setShowSuper] = useState(false);
  const [superHorse, setSuperHorse] = useState(null);

  // Weather state
  const [showWeather, setShowWeather] = useState(false);
  const [weatherData, setWeatherData] = useState(null);
  const [weatherLeaving, setWeatherLeaving] = useState(false);
  const [weatherActive, setWeatherActive] = useState(false);
  
  // Poster animation state
  const [posterLeaving, setPosterLeaving] = useState(false);
  const [posterActive, setPosterActive] = useState(false);
  const lastActivePosterRef = useRef(null); // 'opening' | 'closing'

  // Navnsuper state
  const [showNavnsuper, setShowNavnsuper] = useState(false);
  const [navnsuperData, setNavnsuperData] = useState(null);

  // Crawl state
  const [showCrawl, setShowCrawl] = useState(false);
  const [crawlText, setCrawlText] = useState('');

  // Clown & King state
  const [showClown, setShowClown] = useState(false);
  const [showKing, setShowKing] = useState(false);

  // Vignett state
  const [showVignett, setShowVignett] = useState(false);
  const [showVignettOut, setShowVignettOut] = useState(false);
  const [showV75Intro, setShowV75Intro] = useState(false);
  const [showV75Outro, setShowV75Outro] = useState(false);

  // Opening poster state
  const [showOpeningPoster, setShowOpeningPoster] = useState(false);
  const [openingPosterText, setOpeningPosterText] = useState('');
  const [posterMainText, setPosterMainText] = useState('Tipssendingen starter snart');

  // Closing poster state
  const [showClosingPoster, setShowClosingPoster] = useState(false);
  const [closingPosterStartTime, setClosingPosterStartTime] = useState('');

  // Sommertravet mode
  const [sommertravetMode, setSommertravetMode] = useState(false);

  // Studio frame mode (Sky News-style layout)
  const [studioMode, setStudioMode] = useState(false);

  // Sponsor carousel
  const [showSponsors, setShowSponsors] = useState(false);
  const [sponsors, setSponsors] = useState([]);

  // All races for the current game (used for auto-rotating odds in studio mode)
  const [allRaces, setAllRaces] = useState(null);

  // Dual bong state
  const [showDualBong, setShowDualBong] = useState(false);

  // Webcam state
  const [useWebcam, setUseWebcam] = useState(false);
  const [webcamDeviceId, setWebcamDeviceId] = useState('');
  const [audioDeviceId, setAudioDeviceId] = useState('');
  const [webcamStream, setWebcamStream] = useState(null);

  // Loop-video state (fullscreen looping video with audio)
  const [showVideo, setShowVideo] = useState(false);
  const [videoVersion, setVideoVersion] = useState(null);

  // Gold wipe transition state (for fullscreen odds/bong)
  const [showWipe, setShowWipe] = useState(false);
  const [wipeToken, setWipeToken] = useState(0);
  const wipeTokenRef = useRef(0);
  const wipeActiveRef = useRef(false);       // true from trigger until cover applied
  const pendingWipeStateRef = useRef(null);  // buffered state to apply at coverage
  const processStateRef = useRef(null);      // exposes processStateUpdate to wipe callbacks
  const wipeInitRef = useRef(false);         // adopt persisted token on first load (no wipe)

  // Animation states for broadcast effects
  const [oddsAnimating, setOddsAnimating] = useState(false);
  const [bongAnimating, setBongAnimating] = useState(false);
  const [superAnimating, setSuperAnimating] = useState(false);
  const [navnsuperAnimating, setNavnsuperAnimating] = useState(false);
  const [oddsVisible, setOddsVisible] = useState(false);
  const [bongVisible, setBongVisible] = useState(false);
  const [superVisible, setSuperVisible] = useState(false);
  const [navnsuperVisible, setNavnsuperVisible] = useState(false);
  
  const prevDisplayModeRef = useRef(displayMode);
  const isTransitioningFromFullscreenRef = useRef(false);

  // Arrow indicator for distribution changes
  const renderDistArrow = (horseNumber) => {
    const change = distChanges[horseNumber];
    if (!change) return null;
    return (
      <span style={{
        marginLeft: '4px', fontSize: '0.8em',
        color: change === 'up' ? '#22C55E' : '#EF4444', fontWeight: 700,
      }}>
        {change === 'up' ? '▲' : '▼'}
      </span>
    );
  };

  // Banker fanfare sound
  const bankerAudioRef = useRef(null);
  useEffect(() => {
    if (showBanker && bankerHorse && bankerVisible) {
      if (!bankerAudioRef.current) {
        bankerAudioRef.current = new Audio('/banker-fanfare.mp3');
      }
      bankerAudioRef.current.currentTime = 0;
      bankerAudioRef.current.play().catch(() => {});
    }
  }, [showBanker, bankerHorse, bankerVisible]);

  // Message popup visibility
  useEffect(() => {
    let timer;
    if (showMessage && messageText) {
      setMessageExiting(false);
      setMessageVisible(true);
    } else if (!showMessage && messageVisible) {
      setMessageExiting(true);
      timer = setTimeout(() => { setMessageVisible(false); setMessageExiting(false); }, 800);
    }
    return () => clearTimeout(timer);
  }, [showMessage, messageText, messageVisible]);

  // V75 popup visibility
  useEffect(() => {
    let timer;
    if (showV75 && v75Text) {
      setV75Exiting(false);
      setV75Visible(true);
    } else if (!showV75 && v75Visible) {
      setV75Exiting(true);
      timer = setTimeout(() => { setV75Visible(false); setV75Exiting(false); }, 800);
    }
    return () => clearTimeout(timer);
  }, [showV75, v75Text, v75Visible]);

  // Results overlay - no animation, instant show/hide
  useEffect(() => {
    if (showResults && resultsData) {
      setResultsVisible(true);
    } else {
      setResultsVisible(false);
    }
  }, [showResults, resultsData]);


  // ---- FULLSCREEN / DISPLAY MODE TRANSITIONS ----
  useEffect(() => {
    const wasFullscreen = prevDisplayModeRef.current === 'fullscreen';
    const isNowFullscreen = displayMode === 'fullscreen';
    const isNowOverlay = displayMode === 'overlay';
    
    if (isNowFullscreen) {
      setOddsVisible(false); setOddsAnimating(false);
      setBongVisible(false); setBongAnimating(false);
      isTransitioningFromFullscreenRef.current = false;
    }
    
    if (wasFullscreen && isNowOverlay) {
      isTransitioningFromFullscreenRef.current = true;
      setOddsVisible(false); setOddsAnimating(false);
      setBongVisible(false); setBongAnimating(false);
      setTimeout(() => { isTransitioningFromFullscreenRef.current = false; }, 100);
    }
    
    prevDisplayModeRef.current = displayMode;
  }, [displayMode]);

  // Green odds exit animation
  useEffect(() => {
    if (greenOddsActive && (displayMode !== 'fullscreen' || fullscreenContent !== 'odds_green')) {
      setGreenOddsLeaving(true);
      const timer = setTimeout(() => { setGreenOddsLeaving(false); setGreenOddsActive(false); }, 1800);
      return () => clearTimeout(timer);
    }
    if (displayMode === 'fullscreen' && fullscreenContent === 'odds_green' && !greenOddsActive) {
      setGreenOddsActive(true);
    }
  }, [displayMode, fullscreenContent, greenOddsActive]);

  // Green bong exit animation
  useEffect(() => {
    if (greenBongActive && (displayMode !== 'fullscreen' || fullscreenContent !== 'bong_green')) {
      setGreenBongLeaving(true);
      const timer = setTimeout(() => { setGreenBongLeaving(false); setGreenBongActive(false); }, 1800);
      return () => clearTimeout(timer);
    }
    if (displayMode === 'fullscreen' && fullscreenContent === 'bong_green' && !greenBongActive) {
      setGreenBongActive(true);
    }
  }, [displayMode, fullscreenContent, greenBongActive]);

  // Weather exit animation
  useEffect(() => {
    if (weatherActive && !showWeather) {
      setWeatherLeaving(true);
      const timer = setTimeout(() => { setWeatherLeaving(false); setWeatherActive(false); }, 1800);
      return () => clearTimeout(timer);
    }
    if (showWeather && weatherData && !weatherActive) {
      setWeatherActive(true);
    }
  }, [showWeather, weatherData, weatherActive]);

  // Poster exit animation
  useEffect(() => {
    if (posterActive && !showOpeningPoster && !showClosingPoster) {
      setPosterLeaving(true);
      const timer = setTimeout(() => { setPosterLeaving(false); setPosterActive(false); }, 1800);
      return () => clearTimeout(timer);
    }
    if ((showOpeningPoster || showClosingPoster) && !posterActive) {
      setPosterActive(true);
      lastActivePosterRef.current = showOpeningPoster ? 'opening' : 'closing';
    }
  }, [showOpeningPoster, showClosingPoster, posterActive]);

  // Odds animation (overlay mode only)
  useEffect(() => {
    if (displayMode === 'fullscreen' || isTransitioningFromFullscreenRef.current) return;
    let timer;
    if (showOdds && !oddsVisible) {
      setOddsAnimating(true); setOddsVisible(true);
      timer = setTimeout(() => setOddsAnimating(false), 600);
    } else if (!showOdds && oddsVisible) {
      setOddsAnimating(true);
      timer = setTimeout(() => { setOddsVisible(false); setOddsAnimating(false); }, 450);
    }
    return () => clearTimeout(timer);
  }, [showOdds, oddsVisible, displayMode]);

  // Bong animation (overlay mode only)
  useEffect(() => {
    if (displayMode === 'fullscreen' || isTransitioningFromFullscreenRef.current) return;
    let timer;
    if (showBong && !bongVisible) {
      setBongAnimating(true); setBongVisible(true);
      timer = setTimeout(() => setBongAnimating(false), 700);
    } else if (!showBong && bongVisible) {
      setBongAnimating(true);
      timer = setTimeout(() => { setBongVisible(false); setBongAnimating(false); }, 500);
    }
    return () => clearTimeout(timer);
  }, [showBong, bongVisible, displayMode]);

  // Super animation
  useEffect(() => {
    let timer;
    if (showSuper && superHorse && !superVisible) {
      setSuperAnimating(true); setSuperVisible(true);
      timer = setTimeout(() => setSuperAnimating(false), 800);
    } else if ((!showSuper || !superHorse) && superVisible) {
      setSuperAnimating(true);
      timer = setTimeout(() => { setSuperVisible(false); setSuperAnimating(false); }, 600);
    }
    return () => clearTimeout(timer);
  }, [showSuper, superHorse, superVisible]);

  // Navnsuper animation
  useEffect(() => {
    let timer;
    if (showNavnsuper && navnsuperData && !navnsuperVisible) {
      setNavnsuperAnimating(true); setNavnsuperVisible(true);
      timer = setTimeout(() => setNavnsuperAnimating(false), 600);
    } else if ((!showNavnsuper || !navnsuperData) && navnsuperVisible) {
      setNavnsuperAnimating(true);
      timer = setTimeout(() => { setNavnsuperVisible(false); setNavnsuperAnimating(false); }, 500);
    }
    return () => clearTimeout(timer);
  }, [showNavnsuper, navnsuperData, navnsuperVisible]);

  // ---- WEBCAM is handled by WebcamBackground component ----

  // ---- FULLSCREEN TOGGLE ----
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false));
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  useEffect(() => {
    if (searchParams.get("autoFullscreen") === "true" && !isFullscreen) {
      const handleClick = () => {
        document.documentElement.requestFullscreen().catch(() => {});
        document.removeEventListener('click', handleClick);
      };
      document.addEventListener('click', handleClick, { once: true });
    }
  }, [searchParams, isFullscreen]);
  
  // ---- INITIAL STATE FROM URL/LOCALSTORAGE ----
  useEffect(() => {
    const urlGameId = searchParams.get("game");
    const urlLeg = parseInt(searchParams.get("leg") || "1", 10);
    const urlMode = searchParams.get("mode");
    
    if (urlGameId) {
      setCurrentGameId(urlGameId);
      setCurrentLeg(urlLeg);
      if (urlMode) setDisplayMode(urlMode);
    } else {
      const checkStorage = () => {
        const stored = localStorage.getItem('atg-overlay-selection');
        if (stored) {
          try {
            const selection = JSON.parse(stored);
            if (selection.gameId) {
              setCurrentGameId(selection.gameId);
              setCurrentLeg(selection.leg || 1);
              if (selection.mode) setDisplayMode(selection.mode);
              return true;
            }
          } catch (e) {}
        }
        return false;
      };
      if (!checkStorage()) {
        const pollInit = setInterval(() => { if (checkStorage()) clearInterval(pollInit); }, 200);
        setTimeout(() => clearInterval(pollInit), 30000);
      }
    }
  }, [searchParams]);

  // ---- WEBSOCKET + POLLING STATE SYNC ----
  useEffect(() => {
    let lastTimestamp = 0;
    let ws = null;
    let reconnectAttempts = 0;
    let pollInterval = null;              // fast polling — active only while WS is down
    let safetyPollInterval = null;        // slow polling — ALWAYS active as belt+suspenders
    let heartbeatInterval = null;
    let reconnectTimer = null;
    let isMounted = true;
    let lastMessageTime = Date.now();
    
    const processStateUpdate = (state) => {
      if (!isMounted) return;

      // On the very first state received, adopt any persisted wipe_token so a
      // freshly-loaded overlay does NOT replay an old transition.
      if (!wipeInitRef.current) {
        wipeInitRef.current = true;
        if (state.wipe_token) wipeTokenRef.current = state.wipe_token;
      }

      // --- Gold wipe transition interception ---
      // When a new wipe_token arrives, defer the whole fullscreen switch until
      // the gold fully covers the screen (frame-accurate, driven by the video).
      if (state.wipe_token && state.wipe_token !== wipeTokenRef.current) {
        wipeTokenRef.current = state.wipe_token;
        wipeActiveRef.current = true;
        const pending = { ...state };
        delete pending.wipe_token;
        delete pending.timestamp; // so the replay isn't rejected by the ts guard
        pendingWipeStateRef.current = pending;
        setWipeToken(state.wipe_token);
        setShowWipe(true);
        return; // do NOT apply yet
      }
      // While a wipe is covering, buffer any further updates (incl. the cross-pod
      // full-state rebroadcast) so nothing changes on screen before coverage.
      if (wipeActiveRef.current) {
        const merged = { ...(pendingWipeStateRef.current || {}), ...state };
        delete merged.wipe_token;
        delete merged.timestamp;
        pendingWipeStateRef.current = merged;
        return;
      }

      if (state.timestamp && state.timestamp <= lastTimestamp) return;
      if (state.timestamp) lastTimestamp = state.timestamp;
      
      if (state.game_id) setCurrentGameId(state.game_id);
      if (state.leg) setCurrentLeg(state.leg);
      if (state.mode) setDisplayMode(state.mode);
      if (state.show_bong !== undefined) setShowBong(state.show_bong);
      if (state.bong_display_leg !== undefined) {
        const legs = state.bong_display_leg;
        setBongSelectedLegs(Array.isArray(legs) ? legs : legs ? [legs] : []);
      }
      if (state.show_odds !== undefined) setShowOdds(state.show_odds);
      if (state.fullscreen_content) setFullscreenContent(state.fullscreen_content);
      if (state.show_super !== undefined) setShowSuper(state.show_super);
      if (state.super_horse) setSuperHorse(state.super_horse);
      if (state.show_weather !== undefined) setShowWeather(state.show_weather);
      if (state.weather_data) setWeatherData(state.weather_data);
      if (state.bong_data) setBongData(state.bong_data);
      if (state.bong_data_guest) setBongDataGuest(state.bong_data_guest);
      if (state.show_navnsuper !== undefined) setShowNavnsuper(state.show_navnsuper);
      if (state.navnsuper_data) setNavnsuperData(state.navnsuper_data);
      if (state.use_webcam !== undefined) setUseWebcam(state.use_webcam);
      if (state.webcam_device_id) setWebcamDeviceId(state.webcam_device_id);
      if (state.audio_device_id) setAudioDeviceId(state.audio_device_id);
      if (state.show_video !== undefined) setShowVideo(state.show_video);
      if (state.video_version !== undefined) setVideoVersion(state.video_version);
      if (state.show_crawl !== undefined) setShowCrawl(state.show_crawl);
      if (state.crawl_text !== undefined) setCrawlText(state.crawl_text);
      if (state.show_banker !== undefined) setShowBanker(state.show_banker);
      if (state.banker_horse) setBankerHorse(state.banker_horse);
      if (state.show_message !== undefined) setShowMessage(state.show_message);
      if (state.message_text !== undefined) setMessageText(state.message_text || '');

      if (state.show_v75 !== undefined) setShowV75(state.show_v75);
      if (state.v75_text !== undefined) setV75Text(state.v75_text || '');

      // Results overlay
      if (state.show_results !== undefined) setShowResults(state.show_results);
      if (state.results_data !== undefined) setResultsData(state.results_data);

      // Clown mode
      if (state.show_clown !== undefined) {
        if (state.show_clown) {
          setShowOdds(false);
          setShowBong(false);
          setShowSuper(false);
          setShowNavnsuper(false);
          setShowKing(false);
          setTimeout(() => setShowClown(true), 400);
        } else {
          setShowClown(false);
        }
      }

      // King mode
      if (state.show_king !== undefined) {
        if (state.show_king) {
          setShowOdds(false);
          setShowBong(false);
          setShowSuper(false);
          setShowNavnsuper(false);
          setShowClown(false);
          setTimeout(() => setShowKing(true), 400);
        } else {
          setShowKing(false);
        }
      }

      // Vignett
      if (state.show_vignett !== undefined) setShowVignett(state.show_vignett);
      if (state.show_vignett_out !== undefined) setShowVignettOut(state.show_vignett_out);
      if (state.show_v75_intro !== undefined) setShowV75Intro(state.show_v75_intro);
      if (state.show_v75_outro !== undefined) setShowV75Outro(state.show_v75_outro);

      // Opening poster
      if (state.show_opening_poster !== undefined) setShowOpeningPoster(state.show_opening_poster);
      if (state.opening_poster_text !== undefined) setOpeningPosterText(state.opening_poster_text || '');
      if (state.poster_main_text !== undefined) setPosterMainText(state.poster_main_text || 'Tipssendingen starter snart');

      // Closing poster
      if (state.show_closing_poster !== undefined) setShowClosingPoster(state.show_closing_poster);
      if (state.closing_poster_start_time !== undefined) setClosingPosterStartTime(state.closing_poster_start_time || '');

      // Sommertravet mode
      if (state.sommertravet_mode !== undefined) setSommertravetMode(state.sommertravet_mode);

      // Studio frame mode
      if (state.studio_mode !== undefined) setStudioMode(state.studio_mode);

      // Sponsor carousel
      if (state.show_sponsors !== undefined) setShowSponsors(state.show_sponsors);

      // Dual bong
      if (state.show_dual_bong !== undefined) setShowDualBong(state.show_dual_bong);
      if (state.race_data) setRaceData(state.race_data);
      if (state.game_type) setGameType(state.game_type);
      if (state.track_name) setTrackName(state.track_name);
      if (state.leg) setLegNumber(state.leg);
      if (state.race_data?.distance) setRaceDistance(state.race_data.distance);
      if (state.race_data?.start_method) setStartMethod(state.race_data.start_method);
      if (state.race_data?.start_time) setStartTime(state.race_data.start_time);
    };
    processStateRef.current = processStateUpdate;
    
    const pollBackend = async () => {
      if (!isMounted) return;
      try {
        const response = await axios.get(`${API}/overlay-state`);
        processStateUpdate(response.data);
      } catch (error) {
        const stored = localStorage.getItem('atg-overlay-selection');
        if (stored) {
          try {
            const selection = JSON.parse(stored);
            processStateUpdate({
              game_id: selection.gameId, leg: selection.leg, mode: selection.mode,
              show_bong: selection.showBong, show_odds: selection.showOdds,
              fullscreen_content: selection.fullscreenContent,
              show_super: selection.showSuper, super_horse: selection.superHorse,
              show_weather: selection.showWeather, weather_data: selection.weatherData
            });
          } catch (e) {}
        }
        const bongStored = localStorage.getItem('atg-bong-data');
        if (bongStored) { try { setBongData(JSON.parse(bongStored)); } catch (e) {} }
      }
    };
    
    const startPolling = () => {
      if (pollInterval) return;
      pollBackend();
      pollInterval = setInterval(pollBackend, 300);
    };
    const stopPolling = () => { if (pollInterval) { clearInterval(pollInterval); pollInterval = null; } };
    
    const connectWebSocket = () => {
      if (!isMounted) return;
      // Derive WS URL from REACT_APP_BACKEND_URL so preview and production
      // both hit the correct backend proxy path. window.location.host worked
      // in preview but on the production emergent.host proxy the WS upgrade
      // sometimes failed silently and dropped us to slow polling.
      const backend = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const wsUrl = backend.replace(/^http/, 'ws') + '/api/ws/overlay-state';
      try { ws = new WebSocket(wsUrl); } catch (e) { scheduleReconnect(); return; }

      ws.onopen = () => {
        reconnectAttempts = 0;
        lastMessageTime = Date.now();
        stopPolling();
        // Watchdog + client-driven heartbeat.
        // Every 8s we (a) check whether the server has sent ANY message
        // in the last 25s and force-close the socket if not — this is the
        // ONLY way to detect a "zombie" WebSocket where Cloudflare / K8s
        // ingress silently drops the TCP connection so onclose never fires
        // and readyState stays OPEN indefinitely, freezing the overlay.
        // And (b) send a "ping" to keep the connection alive through
        // idle-timeouts.
        if (heartbeatInterval) clearInterval(heartbeatInterval);
        heartbeatInterval = setInterval(() => {
          if (!ws) return;
          if (Date.now() - lastMessageTime > 25000) {
            // Zombie detected — close the socket AND explicitly trigger a
            // reconnect ourselves. In some proxies ws.close() on a dead TCP
            // connection never fires onclose, so we can't rely on that path
            // alone. We proactively tear everything down and schedule
            // reconnect ourselves.
            handleDisconnect(true);
            return;
          }
          if (ws.readyState === WebSocket.OPEN) {
            try { ws.send('ping'); } catch (_) { /* noop */ }
          }
        }, 8000);
      };
      ws.onmessage = (event) => {
        lastMessageTime = Date.now();
        try {
          if (event.data === 'ping') { try { ws.send('pong'); } catch (_) { /* noop */ } return; }
          if (event.data === 'pong') return;
          processStateUpdate(JSON.parse(event.data));
        } catch (_) { /* noop */ }
      };
      ws.onclose = () => { handleDisconnect(false); };
      ws.onerror = () => { /* onclose will follow */ };
    };

    // Tear down current WS + start fast polling + schedule reconnect. Called
    // from both onclose and the zombie-watchdog. Idempotent — safe to call
    // multiple times.
    const handleDisconnect = (fromZombie) => {
      if (heartbeatInterval) { clearInterval(heartbeatInterval); heartbeatInterval = null; }
      if (ws) {
        try {
          // Remove handlers before closing so a stale onclose can't race with us.
          ws.onopen = null; ws.onmessage = null; ws.onclose = null; ws.onerror = null;
          if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
            ws.close();
          }
        } catch (_) { /* noop */ }
        ws = null;
      }
      startPolling();
      scheduleReconnect();
    };

    const scheduleReconnect = () => {
      if (!isMounted) return;
      if (reconnectTimer) return;
      reconnectAttempts++;
      // Exponential-ish delay capped at 5s. Never gives up — for live broadcast
      // it must keep trying forever, even if the backend was down for 10 min.
      const delay = Math.min(500 * reconnectAttempts, 5000);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connectWebSocket();
      }, delay);
    };
    
    pollBackend();
    connectWebSocket();
    // ALWAYS-ON safety polling every 1.5s — runs even while WS is OPEN. This is
    // the ultimate belt-and-suspenders guarantee that the overlay never
    // freezes during a live broadcast: if the WS is somehow silently dead
    // (zombie state that ping-watchdog missed) or drops between watchdog
    // checks, this poll will still refresh state within 1.5s.
    safetyPollInterval = setInterval(pollBackend, 1500);
    return () => {
      isMounted = false;
      if (ws) { try { ws.onopen = ws.onmessage = ws.onclose = ws.onerror = null; ws.close(); } catch (_) { /* noop */ } }
      stopPolling();
      if (safetyPollInterval) { clearInterval(safetyPollInterval); safetyPollInterval = null; }
      if (heartbeatInterval) { clearInterval(heartbeatInterval); heartbeatInterval = null; }
      if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
    };
  }, []);

  // ---- FETCH ALL RACES for auto-rotating studio odds panel ----
  useEffect(() => {
    if (!studioMode || !currentGameId) { setAllRaces(null); return; }
    let mounted = true;
    const fetchAll = async () => {
      try {
        const res = await axios.get(`${API}/game/${currentGameId}`);
        if (mounted && res.data?.races?.length) setAllRaces(res.data.races);
      } catch { /* game may be test data without full game endpoint */ }
    };
    fetchAll();
    const t = setInterval(fetchAll, 60000);
    return () => { mounted = false; clearInterval(t); };
  }, [studioMode, currentGameId]);

  // ---- FETCH SPONSOR LOGOS for studio sponsor carousel ----
  useEffect(() => {
    if (!studioMode || !showSponsors) return;
    let mounted = true;
    const fetchSponsors = async () => {
      try {
        const res = await axios.get(`${API}/sponsors`);
        if (mounted) setSponsors((res.data || []).map(s => ({ ...s, url: `${API}/sponsors/${s.id}/image` })));
      } catch { /* ignore */ }
    };
    fetchSponsors();
    const t = setInterval(fetchSponsors, 60000);
    return () => { mounted = false; clearInterval(t); };
  }, [studioMode, showSponsors]);

  // ---- DERIVED DATA ----
  const sortedHorses = raceData?.horses
    ?.sort((a, b) => b.distribution - a.distribution)
    ?.slice(0, 12) || [];

  // Track distribution changes for arrows
  useEffect(() => {
    if (!raceData?.horses?.length) return;
    const prev = prevDistRef.current;
    const changes = {};
    raceData.horses.forEach(h => {
      if (prev[h.number] !== undefined && h.distribution > 0) {
        const diff = h.distribution - prev[h.number];
        if (diff > 0.3) changes[h.number] = 'up';
        else if (diff < -0.3) changes[h.number] = 'down';
      }
    });
    setDistChanges(changes);
    const newPrev = {};
    raceData.horses.forEach(h => { newPrev[h.number] = h.distribution; });
    prevDistRef.current = newPrev;
  }, [raceData]);

  // Banker alert animation
  useEffect(() => {
    let timer;
    if (showBanker && bankerHorse) {
      setBankerVisible(true);
    } else if (!showBanker && bankerVisible) {
      timer = setTimeout(() => setBankerVisible(false), 600);
    }
    return () => clearTimeout(timer);
  }, [showBanker, bankerHorse, bankerVisible]);

  // Auto-banker tracking
  const prevBongLeg = useRef(null);
  useEffect(() => {
    if (!bongData?.legs || bongSelectedLegs.length === 0 || JSON.stringify(bongSelectedLegs) === JSON.stringify(prevBongLeg.current)) {
      prevBongLeg.current = bongSelectedLegs;
      return;
    }
    prevBongLeg.current = bongSelectedLegs;
  }, [bongSelectedLegs, bongData]);

  // ---- SHARED POPUPS (rendered persistently - never unmount) ----
  // These are rendered OUTSIDE the view conditionals to prevent remounting

  // ---- DETERMINE VIEW CONTENT ----
  // Background color: transparent for vMix/OBS, green for chromakey
  const chromaBg = isVmix ? 'transparent' : '#00FF00';
  const darkText = isVmix ? '#1A1A2E' : '#000000';
  const darkBg = isVmix ? '#1A1A2E' : '#000000';

  const colors = {
    boxBg: '#FFFFFF', boxBorder: 'transparent',
    headerBg: '#1E3A5F', headerText: '#FFFFFF', headerSubtext: '#A0A0A0',
    rowEven: '#FFFFFF', rowOdd: '#F9FAFB',
    text: darkText, subtext: '#555555',
    numberBg: darkBg, numberText: '#FFFFFF',
    shoeBadgeBg: '#DC2626', shoeBadgeText: '#FFFFFF',
    distBarBg: '#E5E5E5', distBarFill: '#F97316', accentColor: '#F97316',
    footerBg: '#F5F5F5', footerText: '#666666',
    borderColor: '#E5E5E5', boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
  };

  // ---- THEME: Sommertravet switches gold → turquoise ----
  const themeVars = sommertravetMode ? {
    '--theme-accent': '#5CE0D6',
    '--theme-accent-rgb': '92,224,214',
    '--theme-accent-dark': '#0A1628',
    '--theme-secondary': '#FF69B4',
    '--theme-panel-bg': '#050F23',
    '--theme-panel-border': 'rgba(92,224,214,0.6)',
    '--theme-panel-border-subtle': 'rgba(92,224,214,0.3)',
    '--theme-panel-border-faint': 'rgba(92,224,214,0.1)',
    '--theme-gradient-line': 'rgba(92,224,214,0.7)',
    '--theme-gradient-line-subtle': 'rgba(92,224,214,0.4)',
    '--theme-header-gradient': 'rgba(92,224,214,0.12)',
  } : {
    '--theme-accent': '#FFD700',
    '--theme-accent-rgb': '255,215,0',
    '--theme-accent-dark': '#0A1628',
    '--theme-secondary': '#FFD700',
    '--theme-panel-bg': '#050F23',
    '--theme-panel-border': 'rgba(255,215,0,0.5)',
    '--theme-panel-border-subtle': 'rgba(255,215,0,0.25)',
    '--theme-panel-border-faint': 'rgba(255,215,0,0.08)',
    '--theme-gradient-line': 'rgba(255,215,0,0.6)',
    '--theme-gradient-line-subtle': 'rgba(255,215,0,0.4)',
    '--theme-header-gradient': 'rgba(255,215,0,0.15)',
  };

  let viewContent = null;

  // Green fullscreen graphics expand the video to full screen even in studio mode.
  // greenOddsActive/greenBongActive are included to bridge the 1-frame gap between
  // the state update (mode='overlay') and the async greenXLeaving effect — otherwise
  // the studio frame flashes in before the exit animation starts.
  const greenActive = greenOddsActive || greenBongActive;
  const greenFullscreen = (displayMode === 'fullscreen' && (fullscreenContent === 'odds_green' || fullscreenContent === 'bong_green')) || greenOddsLeaving || greenBongLeaving || greenActive;

  // Derive opening poster leaving state synchronously (no 1-frame gap)
  const openingPosterLeaving = posterActive && !showOpeningPoster && lastActivePosterRef.current === 'opening';

  if (showOpeningPoster || (posterActive && lastActivePosterRef.current === 'opening')) {
    // ---- VIEW: OPENING POSTER (Åpningsplakat) ----
    viewContent = <OpeningPoster raceDayText={openingPosterText} mainText={posterMainText} isVmix={isVmix} sommertravetMode={sommertravetMode} useWebcam={useWebcam} posterLeaving={openingPosterLeaving} />;
  } else if (showClosingPoster) {
    // ---- VIEW: CLOSING POSTER (Avslutningsplakat) ----
    viewContent = <ClosingPoster raceDayText={openingPosterText} startTime={closingPosterStartTime} sommertravetMode={sommertravetMode} />;
  } else if (!studioMode && ((showWeather && weatherData) || weatherLeaving)) {
    // ---- VIEW: WEATHER OVERLAY (transparent background) ----
    viewContent = <WeatherFullscreen weatherData={weatherData} weatherLeaving={weatherLeaving} isVmix={isVmix} sommertravetMode={sommertravetMode} />;
  } else if (!studioMode && (!currentGameId || !raceData) && displayMode !== 'fullscreen') {
    // ---- VIEW: WAITING STATE ----
    if (showNavnsuper && navnsuperData) {
      viewContent = (
        <div style={{ backgroundColor: useWebcam ? 'transparent' : chromaBg, minHeight: '100vh', width: '100vw', position: 'relative' }}>
          <div
            data-testid="navnsuper-lower-third"
            style={{
              position: 'absolute', bottom: '80px', left: '50%', transform: 'translateX(-50%)',
              backgroundColor: '#FFFFFF', borderRadius: '12px 12px 0 0', padding: '0',
              minWidth: '400px', maxWidth: '600px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              overflow: 'hidden', zIndex: 1,
            }}
          >
            <div style={{
              backgroundColor: '#1E3A5F', color: '#FFFFFF', padding: '0 40px',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px',
              height: '90px', overflow: 'hidden'
            }}>
              <div style={{ width: '160px', height: '80px', flexShrink: 0,
                backgroundImage: 'url(/sorlandet-logo.png)', backgroundSize: 'contain', 
                backgroundRepeat: 'no-repeat', backgroundPosition: 'center' 
              }} />
              <div style={{
                fontFamily: "'Barlow Condensed', sans-serif", fontSize: '2.5rem',
                fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.02em'
              }}>
                {navnsuperData.name}
              </div>
            </div>
            {navnsuperData.title && (
              <div style={{ backgroundColor: '#FFFFFF', color: darkText, padding: '16px 40px', textAlign: 'center' }}>
                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '1.4rem', fontWeight: 500 }}>
                  {navnsuperData.title}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    } else {
      viewContent = (
        <div style={{ 
          backgroundColor: useWebcam ? 'transparent' : (displayMode === 'fullscreen' ? '#020617' : chromaBg), 
          minHeight: '100vh', width: '100vw', display: 'flex', alignItems: 'center',
          justifyContent: 'center', position: 'relative'
        }}>
          <div style={{
            backgroundColor: '#FFFFFF', padding: '40px', borderRadius: '8px',
            fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.5rem',
            color: '#666666', textAlign: 'center', zIndex: 1,
          }}>
            Venter på valg fra kontrollpanel...
          </div>
        </div>
      );
    }
  } else if ((!studioMode || greenFullscreen) && (displayMode === 'fullscreen' || greenOddsLeaving || greenBongLeaving || (studioMode && greenActive))) {
    // ---- VIEW: FULLSCREEN MODE ----
    if (!raceData) {
      viewContent = (
        <div style={{
          backgroundImage: useWebcam ? 'none' : 'url(https://customer-assets.emergentagent.com/job_travpark-odds-bot/artifacts/wgi1xszn_Messenger_creation_92991457-B06E-4C2E-9E00-6C9A171E0ACD.jpeg)',
          backgroundSize: 'cover', backgroundPosition: 'center',
          backgroundColor: useWebcam ? 'transparent' : 'transparent',
          height: '100vh', width: '100vw', display: 'flex',
          alignItems: 'center', justifyContent: 'center', position: 'relative'
        }}>
          <div style={{
            backgroundColor: 'rgba(255,255,255,0.9)', padding: '40px', borderRadius: '12px',
            fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.5rem', color: '#333', zIndex: 1,
          }}>
            Laster data...
          </div>
        </div>
      );
    } else if ((fullscreenContent === 'bong' || fullscreenContent === 'bong_green' || greenBongLeaving) && bongData) {
      const isGreenBong = fullscreenContent === 'bong_green' || greenBongLeaving;
      viewContent = (
        <FullscreenBong
          bongData={bongData} bongDataGuest={showDualBong ? bongDataGuest : null} trackName={trackName} useWebcam={useWebcam} webcamDeviceId={webcamDeviceId} audioDeviceId={audioDeviceId}
          isGreenBong={isGreenBong} greenBongLeaving={greenBongLeaving} bongSelectedLegs={bongSelectedLegs}
          isVmix={isVmix} sommertravetMode={sommertravetMode}
        />
      );
    } else {
      const isGreenOdds = fullscreenContent === 'odds_green' || greenOddsLeaving;
      viewContent = (
        <FullscreenOdds
          raceData={raceData} sortedHorses={sortedHorses}
          gameType={gameType} legNumber={legNumber} trackName={trackName}
          raceDistance={raceDistance} startMethod={startMethod} startTime={startTime}
          flashingHorses={flashingHorses} distChanges={distChanges}
          useWebcam={useWebcam} webcamDeviceId={webcamDeviceId} audioDeviceId={audioDeviceId}
          isGreenOdds={isGreenOdds} greenOddsLeaving={greenOddsLeaving}
          renderDistArrow={renderDistArrow}
          isVmix={isVmix}
          sommertravetMode={sommertravetMode}
        />
      );
    }
  } else if (studioMode) {
    // ---- VIEW: STUDIO FRAME MODE (Sky News-style layout) ----
    // Fullscreen odds/bong render INSIDE the camera window (scaled to 0.76 = cam rect size)
    const studioFullscreen = displayMode === 'fullscreen' || greenOddsLeaving || greenBongLeaving;
    let studioCamContent = null;
    if (studioFullscreen && raceData) {
      if ((fullscreenContent === 'bong' || fullscreenContent === 'bong_green' || greenBongLeaving) && bongData) {
        const isGreenBong = fullscreenContent === 'bong_green' || greenBongLeaving;
        studioCamContent = (
          <FullscreenBong
            bongData={bongData} bongDataGuest={showDualBong ? bongDataGuest : null} trackName={trackName} useWebcam={useWebcam} webcamDeviceId={webcamDeviceId} audioDeviceId={audioDeviceId}
            isGreenBong={isGreenBong} greenBongLeaving={greenBongLeaving} bongSelectedLegs={bongSelectedLegs}
            isVmix={isVmix} sommertravetMode={sommertravetMode}
          />
        );
      } else {
        const isGreenOdds = fullscreenContent === 'odds_green' || greenOddsLeaving;
        studioCamContent = (
          <FullscreenOdds
            raceData={raceData} sortedHorses={sortedHorses}
            gameType={gameType} legNumber={legNumber} trackName={trackName}
            raceDistance={raceDistance} startMethod={startMethod} startTime={startTime}
            flashingHorses={flashingHorses} distChanges={distChanges}
            useWebcam={useWebcam} webcamDeviceId={webcamDeviceId} audioDeviceId={audioDeviceId}
            isGreenOdds={isGreenOdds} greenOddsLeaving={greenOddsLeaving}
            renderDistArrow={renderDistArrow}
            isVmix={isVmix}
            sommertravetMode={sommertravetMode}
          />
        );
      }
    }
    viewContent = (
      <div
        className="overlay-screen"
        data-testid="overlay-screen-studio"
        onDoubleClick={toggleFullscreen}
        style={{
          position: 'relative', minHeight: '100vh', width: '100vw',
          backgroundColor: 'transparent',
          overflow: 'hidden', cursor: 'pointer', ...themeVars,
        }}
      >
        <StudioFrame
          isVmix={isVmix} useWebcam={useWebcam}
          sortedHorses={sortedHorses} gameType={gameType} legNumber={legNumber}
          trackName={trackName} raceDistance={raceDistance} startMethod={startMethod} startTime={startTime}
          showOdds={showOdds} showBong={showBong} bongData={bongData} bongDataGuest={bongDataGuest} showDualBong={showDualBong}
          bongSelectedLegs={bongSelectedLegs}
          weatherData={weatherData} flashingHorses={flashingHorses} renderDistArrow={renderDistArrow}
          sommertravetMode={sommertravetMode}
          showCrawl={showCrawl} crawlText={crawlText} showWeather={showWeather}
          allRaces={allRaces} currentLeg={currentLeg}
          showMessage={showMessage} messageText={messageText}
          sponsors={showSponsors ? sponsors : []}
        />

        {/* Fullscreen graphics clipped and scaled into the camera window */}
        {studioCamContent && (
          <div style={{
            position: 'absolute', ...STUDIO_CAM_RECT, overflow: 'hidden',
            borderRadius: '10px', zIndex: 3, pointerEvents: 'none',
          }}>
            <div style={{ width: '100vw', height: '100vh', transform: 'scale(0.76)', transformOrigin: 'top left' }}>
              {studioCamContent}
            </div>
          </div>
        )}

        {/* Lower thirds inside the camera window */}
        <div style={{
          position: 'absolute', ...STUDIO_CAM_RECT, overflow: 'hidden',
          borderRadius: '10px', zIndex: 4, pointerEvents: 'none',
        }}>
          <OverlaySuperLowerThird
            superVisible={superVisible} showSuper={showSuper}
            superHorse={superHorse} colors={colors}
            sommertravetMode={sommertravetMode}
          />

          <OverlayNavnsuperLowerThird
            navnsuperVisible={navnsuperVisible} showNavnsuper={showNavnsuper}
            navnsuperData={navnsuperData} colors={colors}
            sommertravetMode={sommertravetMode}
          />
        </div>
      </div>
    );
  } else {
    // ---- VIEW: CHROMAKEY OVERLAY MODE ----
    viewContent = (
      <div 
        className="overlay-screen"
        data-testid="overlay-screen"
        onDoubleClick={toggleFullscreen}
        style={{ 
          backgroundColor: useWebcam ? 'transparent' : chromaBg, 
          minHeight: '100vh', width: '100vw', overflow: 'hidden',
          position: 'relative', cursor: 'pointer',
          ...themeVars,
        }}
      >

        <OverlayBongDisplay
          bongVisible={bongVisible} showBong={showBong}
          bongData={bongData} bongDataGuest={showDualBong ? bongDataGuest : null} bongSelectedLegs={bongSelectedLegs} colors={colors}
          sommertravetMode={sommertravetMode}
        />

        <OverlaySuperLowerThird
          superVisible={superVisible} showSuper={showSuper}
          superHorse={superHorse} colors={colors}
          sommertravetMode={sommertravetMode}
        />

        <OverlayNavnsuperLowerThird
          navnsuperVisible={navnsuperVisible} showNavnsuper={showNavnsuper}
          navnsuperData={navnsuperData} colors={colors}
          sommertravetMode={sommertravetMode}
        />
      </div>
    );
  }

  // ---- SINGLE RETURN: View content + persistent overlays ----
  const posterShowing = showOpeningPoster || showClosingPoster || posterActive;
  const studioLayoutActive = studioMode && !posterShowing && !greenFullscreen;
  const rootBg = useWebcam ? 'transparent' : (isVmix ? 'transparent' : '#00FF00');
  return (
    <div style={{ 
      position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden',
      backgroundColor: rootBg,
      '--text-dark': isVmix ? '#1A1A2E' : '#000000',
      '--bg-dark': isVmix ? '#1A1A2E' : '#000000',
      ...themeVars,
    }}>
      {/* Persistent webcam layer — never unmounts/remounts during view transitions.
          In studio mode the video is clipped to the camera window rect, and expands
          smoothly to fullscreen when green fullscreen graphics are shown. */}
      {useWebcam && (
        <div style={{
          position: 'absolute',
          top: studioLayoutActive ? STUDIO_CAM_RECT.top : '0px',
          left: studioLayoutActive ? STUDIO_CAM_RECT.left : '0px',
          width: studioLayoutActive ? STUDIO_CAM_RECT.width : '100vw',
          height: studioLayoutActive ? STUDIO_CAM_RECT.height : '100vh',
          overflow: 'hidden',
          borderRadius: studioLayoutActive ? '10px' : '0px',
          zIndex: 0,
          transition: studioMode
            ? 'top 0.8s cubic-bezier(0.22,1,0.36,1), left 0.8s cubic-bezier(0.22,1,0.36,1), width 0.8s cubic-bezier(0.22,1,0.36,1), height 0.8s cubic-bezier(0.22,1,0.36,1), border-radius 0.8s cubic-bezier(0.22,1,0.36,1)'
            : 'none',
        }}>
          <WebcamBackground useWebcam={useWebcam} webcamDeviceId={webcamDeviceId} audioDeviceId={audioDeviceId} />
        </div>
      )}
      {viewContent}
      {/* Persistent layer — crawl ALWAYS visible (in studio mode it lives in the strip under the camera) */}
      <OverlayCrawlBanner showCrawl={showCrawl && !studioLayoutActive} crawlText={crawlText} />
      {displayMode !== 'fullscreen' && !studioMode && (
        <OverlayOddsBox
          oddsVisible={oddsVisible} showOdds={showOdds}
          sortedHorses={sortedHorses} gameType={gameType} legNumber={legNumber}
          trackName={trackName} raceDistance={raceDistance} startMethod={startMethod} startTime={startTime}
          flashingHorses={flashingHorses} colors={colors} renderDistArrow={renderDistArrow}
          sommertravetMode={sommertravetMode}
        />
      )}
      <ClownOverlay showClown={showClown} />
      <KingOverlay showKing={showKing} />
      <BankerAlert bankerVisible={bankerVisible} bankerHorse={bankerHorse} showBanker={showBanker} />
      <MessagePopup messageVisible={messageVisible && !studioMode} messageText={messageText} messageExiting={messageExiting} />
      <V75Popup v75Visible={v75Visible} v75Text={v75Text} v75Exiting={v75Exiting} />
      <ResultsOverlay resultsVisible={resultsVisible} resultsData={resultsData} colors={colors} sommertravetMode={sommertravetMode} />
      <VignettOverlay showVignett={showVignett} variant="in" />
      <VignettOverlay showVignett={showVignettOut} variant="out" />
      <V75Vignett show={showV75Intro} variant="in" />
      <V75Vignett show={showV75Outro} variant="out" />
      <LoopVideoLayer show={showVideo} version={videoVersion} api={API} />
      <WipeTransition
        show={showWipe}
        token={wipeToken}
        src="/wipe.webm"
        coverTime={WIPE_COVER_TIME}
        onCover={() => {
          if (pendingWipeStateRef.current && processStateRef.current) {
            const pending = pendingWipeStateRef.current;
            pendingWipeStateRef.current = null;
            wipeActiveRef.current = false;
            processStateRef.current(pending);
          } else {
            wipeActiveRef.current = false;
          }
        }}
        onEnd={() => setShowWipe(false)}
      />
    </div>
  );
}
